import React, { useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { ExecutiveSummaryData } from './executive-summary';
import { Box } from '@lifeomic/chroma-react/components/Box';
import { Button } from '@lifeomic/chroma-react/components/Button';
import { Modal } from '@lifeomic/chroma-react/components/Modal';
import { Text } from '@lifeomic/chroma-react/components/Text';

const messages = defineMessages({
  status: {
    id: 'phc-web-toolkit.subjectViewer.executiveSummary.status',
    defaultMessage: 'Status',
  },
  signOff: {
    id: 'phc-web-toolkit.subjectViewer.executiveSummary.signOff',
    defaultMessage: 'Sign Off',
  },
  reopen: {
    id: 'phc-web-toolkit.subjectViewer.executiveSummary.reopen',
    defaultMessage: 'Reopen',
  },
  discard: {
    id: 'phc-web-toolkit.subjectViewer.executiveSummary.discard',
    defaultMessage: 'Discard',
  },
  signOffModal: {
    id: 'phc-web-toolkit.subjectViewer.executiveSummary.signOffModal',
    defaultMessage:
      'Are you sure you want to sign off? This report and all referenced documents will be made available to the member.',
  },
  cancel: {
    id: 'phc-web-toolkit.subjectViewer.executiveSummary.cancel',
    defaultMessage: 'Cancel',
  },
  completedButton: {
    id: 'phc-web-toolkit.subjectViewer.executiveSummary.completedButton',
    defaultMessage: 'Sign Off',
  },
  activeButton: {
    id: 'phc-web-toolkit.subjectViewer.executiveSummary.activeButton',
    defaultMessage: 'Reopen',
  },
  draftButton: {
    id: 'phc-web-toolkit.subjectViewer.executiveSummary.draftButton',
    defaultMessage: 'Move to Draft',
  },
  'entered-in-errorButton': {
    id: 'phc-web-toolkit.subjectViewer.executiveSummary.enteredInErrorButton',
    defaultMessage: 'Discard',
  },
  completedModal: {
    id: 'phc-web-toolkit.subjectViewer.executiveSummary.completedModal',
    defaultMessage:
      'Are you sure you want to sign off? This report and all referenced documents will be made available to the member.',
  },
  completedModal2: {
    id: 'phc-web-toolkit.subjectViewer.executiveSummary.completedModal2',
    defaultMessage:
      'Once Signed Off, this report is final and a new Active report can be started.',
  },
  activeModal: {
    id: 'phc-web-toolkit.subjectViewer.executiveSummary.activeModal',
    defaultMessage: 'Are you sure you want to reopen this report?',
  },
  'entered-in-errorModal': {
    id: 'phc-web-toolkit.subjectViewer.executiveSummary.enteredInErrorModal',
    defaultMessage: 'Are you sure you want to discard this report?',
  },
});

export type StatusValue =
  | 'draft'
  | 'active'
  | 'completed'
  | 'entered-in-error'
  | 'unknown';

export const Status = ({
  executiveSummary,
  onStatusChange,
  disabled,
}: {
  executiveSummary: ExecutiveSummaryData;
  onStatusChange: (status: StatusValue) => void;
  disabled?: boolean;
}) => {
  const { formatMessage } = useIntl();

  const [confirmingStatus, setConfirmingStatus] = useState<StatusValue | null>(
    null,
  );

  const confirmChange = () => {
    confirmingStatus && onStatusChange(confirmingStatus);
    setConfirmingStatus(null);
  };

  if (executiveSummary.status === 'completed') {
    return null;
  }

  return (
    <>
      <Modal
        size={0}
        isOpen={!!confirmingStatus}
        onDismiss={() => setConfirmingStatus(null)}
        title={formatMessage(messages.signOff)}
        actions={[
          <Button
            key="cancel"
            variant="outlined"
            onClick={() => setConfirmingStatus(null)}
          >
            {formatMessage(messages.cancel)}
          </Button>,
          <Button key="signOff" onClick={confirmChange}>
            {confirmingStatus &&
              formatMessage(
                messages[`${confirmingStatus}Button` as keyof typeof messages],
              )}
          </Button>,
        ]}
      >
        <Text>
          {confirmingStatus &&
            formatMessage(
              messages[`${confirmingStatus}Modal` as keyof typeof messages],
            )}
        </Text>
        {confirmingStatus &&
          messages[`${confirmingStatus}Modal2` as keyof typeof messages] && (
            <Text style={{ marginTop: 16 }}>
              {formatMessage(
                messages[`${confirmingStatus}Modal2` as keyof typeof messages],
              )}
            </Text>
          )}
      </Modal>
      <Box direction="row" align="center" justify="space-evenly" gap={2}>
        <Button
          fullWidth
          variant="outlined"
          onClick={() =>
            setConfirmingStatus(
              executiveSummary.status === 'entered-in-error'
                ? 'active'
                : 'entered-in-error',
            )
          }
          disabled={disabled}
        >
          {executiveSummary.status === 'entered-in-error'
            ? formatMessage(messages.reopen)
            : formatMessage(messages.discard)}
        </Button>
        <Button
          fullWidth
          color="default"
          onClick={() => setConfirmingStatus('completed')}
          disabled={disabled || executiveSummary.status !== 'active'}
        >
          {formatMessage(messages.signOff)}
        </Button>
      </Box>
    </>
  );
};
